import React from "react";
import { Card } from "@/components/ui/card";
import { PLAN_TYPES } from "@/pages/Planning";

const pct = (a, b) => (b ? Math.min(100, Math.round((a / b) * 100)) : null);

function Bar({ actual, planned, target, grad }) {
  const a = pct(actual, target), p = pct(planned, target);
  if (a == null) return null;
  return (
    <div className="h-1.5 rounded-full bg-slate-100 overflow-hidden relative">
      {p != null && <div className="absolute inset-y-0 left-0 bg-slate-300 rounded-full" style={{ width: `${p}%` }} />}
      <div className={`absolute inset-y-0 left-0 rounded-full bg-gradient-to-r ${grad}`} style={{ width: `${a}%` }} />
    </div>
  );
}

export default function QuarterView({ data, onOpenMonth }) {
  if (!data) return <Card className="p-8 text-center text-slate-400 border-slate-200 shadow-none">Loading quarter…</Card>;
  const types = PLAN_TYPES;
  return (
    <div className="space-y-5" data-testid="plan-quarter-view">
      <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-3" data-testid="quarter-tiles">
        {types.map(t => {
          const m = data.quarter[t.metric];
          const Icon = t.icon;
          const hit = m.target != null && m.actual >= m.target;
          const onPace = m.target != null && m.actual + (m.planned - m.done) >= m.target;
          return (
            <div key={t.value} className={`relative overflow-hidden rounded-xl p-4 text-white bg-gradient-to-br ${t.grad} shadow-md`} data-testid={`quarter-tile-${t.value}`}>
              <Icon className="absolute -right-3 -bottom-3 w-20 h-20 text-white/15" />
              <div className="flex items-center gap-1.5"><Icon className="w-3.5 h-3.5 text-white/90" /><div className="text-[10px] uppercase tracking-widest font-mono-plex text-white/85 truncate">{t.short}</div></div>
              <div className="mt-2 flex items-baseline gap-1.5"><span className="font-heading font-black text-4xl leading-none drop-shadow-sm">{m.actual}</span><span className="text-[11px] text-white/85 font-mono-plex">done{m.target != null ? ` / ${m.target}` : ""}</span></div>
              {m.target != null && (
                <div className="mt-2 h-1.5 rounded-full bg-white/25 overflow-hidden relative">
                  <div className="absolute inset-y-0 left-0 bg-white/40 rounded-full" style={{ width: `${pct(m.actual + (m.planned - m.done), m.target)}%` }} />
                  <div className="absolute inset-y-0 left-0 bg-white/95 rounded-full" style={{ width: `${pct(m.actual, m.target)}%` }} />
                </div>
              )}
              <div className="mt-1.5 text-[11px] font-mono-plex text-white/90 flex items-center justify-between gap-2">
                <span>{m.planned} planned</span>
                {m.target != null && <span className="px-1.5 py-0.5 rounded-full bg-white/25 font-bold">{hit ? "target hit" : onPace ? "on pace" : `${m.target - m.actual - (m.planned - m.done)} to plan`}</span>}
              </div>
            </div>
          );
        })}
      </div>

      <Card className="border-slate-200 shadow-none overflow-x-auto">
        <table className="w-full text-sm" data-testid="quarter-table">
          <thead className="bg-slate-50 text-left text-xs uppercase tracking-widest font-mono-plex text-slate-500">
            <tr><th className="p-3">Month</th>{types.map(t => <th key={t.value} className="p-3">{t.short}</th>)}</tr>
          </thead>
          <tbody>
            {data.months.map(mo => (
              <tr key={mo.start} className="border-t border-slate-100 hover:bg-slate-50/70 cursor-pointer transition-colors" onClick={() => onOpenMonth(mo.start)} data-testid={`quarter-month-${mo.start.slice(0, 7)}`} title="Open this month in Planning">
                <td className="p-3">
                  <div className="font-heading font-black text-lg text-slate-900">{mo.label}</div>
                  <div className="text-[10px] font-mono-plex text-slate-400">{mo.start} → {mo.end}</div>
                </td>
                {types.map(t => {
                  const m = mo.metrics[t.metric];
                  const hit = m.target != null && m.actual >= m.target;
                  return (
                    <td key={t.value} className="p-3 align-top" data-testid={`quarter-cell-${mo.start.slice(0, 7)}-${t.metric}`}>
                      <div className="flex items-baseline gap-1">
                        <span className={`font-heading font-black text-xl ${hit ? "text-emerald-700" : "text-slate-900"}`}>{m.actual}</span>
                        {m.target != null && <span className="text-xs text-slate-400 font-mono-plex">/ {m.target}</span>}
                      </div>
                      <div className="w-28 mt-1"><Bar actual={m.actual} planned={m.actual + (m.planned - m.done)} target={m.target} grad={t.grad} /></div>
                      <div className="text-[10px] text-slate-500 font-mono-plex mt-1">{m.planned} planned · {m.done} done</div>
                    </td>
                  );
                })}
              </tr>
            ))}
            <tr className="border-t-2 border-slate-900 bg-slate-50 font-semibold" data-testid="quarter-total-row">
              <td className="p-3"><div className="text-slate-900">{data.label} total</div><div className="text-[10px] font-mono-plex text-slate-500 font-normal">{data.reps} rep{data.reps === 1 ? "" : "s"}</div></td>
              {types.map(t => { const m = data.quarter[t.metric]; return <td key={t.value} className="p-3 align-top"><span className={`font-heading font-black text-xl ${m.target != null && m.actual >= m.target ? "text-emerald-700" : "text-slate-900"}`}>{m.actual}</span>{m.target != null && <span className="text-xs text-slate-400 font-mono-plex"> / {m.target}</span>}</td>; })}
            </tr>
          </tbody>
        </table>
      </Card>
      <p className="text-[11px] text-slate-400 font-mono-plex">Bars: solid = done, grey = still planned. Click a month to drill into it.</p>
    </div>
  );
}
